import React from "react";

import {
  CalendarToday as CalendarIcon,
  Category as TypeIcon,
  CheckCircle as ResolvedIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";

import { ComplaintItem } from "../../../api/PartnerPortalGatewayApi";

interface FeedbackCardProps {
  feedback: ComplaintItem;
}

export const FeedbackCard: React.FC<FeedbackCardProps> = ({ feedback }) => {
  const formatDate = (date: dayjs.Dayjs | undefined | null): string => {
    return date ? date.format("DD-MMM-YYYY") : "-";
  };

  const getResolvedDisplay = (): string => {
    // Legacy logic: if ComplaintTypeId == 1, show 'n/a'
    if (feedback.complaintTypeId === 1) {
      return "n/a";
    }
    return formatDate(feedback.resolutionDate);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: "2px solid",
        borderColor: "grey.300",
        transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
          borderColor: "primary.main",
        },
      }}
    >
      <CardHeader
        title={feedback.complaintType || "Feedback"}
        slotProps={{
          title: {
            variant: "subtitle1",
            fontWeight: 600,
            sx: { wordBreak: "break-word" },
          },
        }}
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {feedback.primaryContactName || "N/A"}
          </Typography>
        }
        action={
          <Chip
            icon={<CalendarIcon sx={{ fontSize: 14 }} />}
            label={formatDate(feedback.complaintDate)}
            size="small"
            sx={{ backgroundColor: "#f5f5f5" }}
          />
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ flex: 1, pt: 1 }}>
        <List dense disablePadding>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PersonIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
              }
              secondary={<Typography variant="body2">{feedback.primaryContactName || "-"}</Typography>}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CalendarIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Raised
                </Typography>
              }
              secondary={<Typography variant="body2">{formatDate(feedback.complaintDate)}</Typography>}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <TypeIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
              }
              secondary={<Typography variant="body2">{feedback.complaintType || "-"}</Typography>}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ResolvedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Resolved
                </Typography>
              }
              secondary={<Typography variant="body2">{getResolvedDisplay()}</Typography>}
            />
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};
